import { useEffect, useRef } from 'react'
import { notification } from 'antd'
import { useMutation } from '@tanstack/react-query'
import {
    addRobotLocation,
    deleteRobotLocation,

} from '@/api/robot-locations.api'
import type { Position, SlamPoint } from '../../robot-position-setup/types'

interface UsePositionLocationMutationsOptions {
    robotId: string
    pendingClickCoords: SlamPoint | null
    setPendingClickCoords: React.Dispatch<React.SetStateAction<SlamPoint | null>>
    positionsRef: React.RefObject<Position[]>
    setPositions: React.Dispatch<React.SetStateAction<Position[]>>
    setSelectedCoordinate: React.Dispatch<React.SetStateAction<SlamPoint | null>>
    setSelectedPositionId: React.Dispatch<React.SetStateAction<string>>
    setDirectionRotateMode: React.Dispatch<React.SetStateAction<boolean>>
    getRawPointFromPosition: (
        p: Pick<Position, 'x' | 'y' | 'rawX' | 'rawY'>,
    ) => SlamPoint
    toRawPoint: (p: SlamPoint) => SlamPoint
    onAddLocationSuccess?: (createdId: string) => void
}

export function usePositionLocationMutations({
    robotId,
    pendingClickCoords,
    setPendingClickCoords,
    setPositions,
    setSelectedCoordinate,
    setSelectedPositionId,
    onAddLocationSuccess,
}: UsePositionLocationMutationsOptions) {
    const yawCommitTimerRef = useRef<number | null>(null)

    const { mutateAsync: addLocation, isPending: isAddingLocation } = useMutation({
        mutationFn: ({
            rid,
            name,
            x,
            y,
            yaw,
        }: {
            rid: string
            name?: string
            x: number
            y: number
            yaw?: number
        }) =>
            addRobotLocation(rid, {
                name: name || 'unknown',
                longitude: x,
                latitude: y,
                yaw: yaw ?? 0,
            }),
    })

    const { mutate: deleteLocation, isPending: isDeletingLocation } = useMutation({
        mutationFn: ({ rid, id }: { rid: string; id: string }) =>
            deleteRobotLocation(rid, id),
        onSuccess: (_data, { id }) => {
            setPositions((prev) => prev.filter((p) => p.id !== id))
            setSelectedPositionId((prev) => (prev === id ? '' : prev))
            notification.success({ title: 'Xóa vị trí thành công!' })
        },
    })

    const handleMenuAddNew = async (newLocationData?: {
        name?: string
        yaw?: number
        coords?: SlamPoint | null
    }): Promise<string | null> => {
        const rawPoint = newLocationData?.coords || pendingClickCoords
        if (!rawPoint || !robotId) return null

        const x = Number(rawPoint.x.toFixed(6))
        const y = Number(rawPoint.y.toFixed(6))
        const name = newLocationData?.name?.trim() || 'unknown'
        const yaw = newLocationData?.yaw ?? 0

        try {
            const created = await addLocation({
                rid: robotId,
                name,
                x,
                y,
                yaw: yaw,
            })

            setPositions((prev) => [
                ...prev,
                {
                    id: created.id,
                    name: created.name || name,
                    x: rawPoint.x,
                    y: rawPoint.y,
                    rawX: x,
                    rawY: y,
                    yaw: yaw,
                    timeRanges: [],
                    scheduleLoaded: true,
                },
            ])
            setSelectedCoordinate({ x, y })
            setSelectedPositionId(created.id)
            onAddLocationSuccess?.(created.id)
            return created.id
        } finally {
            setPendingClickCoords(null)
        }
    }

    const handleDeletePosition = (posId: string) => {
        if (!robotId) return
        deleteLocation({ rid: robotId, id: posId })
    }

    const handleMarkerYawChange = (posId: string, yaw: number) => {
        setPositions((prev) =>
            prev.map((p) => (p.id === posId ? { ...p, yaw } : p)),
        )
    }


    useEffect(() => {
        return () => {
            if (yawCommitTimerRef.current !== null) {
                window.clearTimeout(yawCommitTimerRef.current)
            }
        }
    }, [])

    return {
        isAddingLocation,
        isDeletingLocation,
        handleDeletePosition,
        handleMarkerYawChange,
        handleMenuAddNew,
    }
}
